import { httpClient } from '@alfa-bank/site-http-client';
import { DADATA_PROXY_SUGGEST_ADDRESS } from '@alfa-bank/site-envconfig';

import type {
    IAddressResponse,
    ISuggestions,
    IResponseSuggestionData,
    IFormattedSuggestionData,
} from './models';

const EMPTY_MESSAGE = 'Поле обязательно для заполнения';
const DATA_SUFFIX = '-dadata';

const convertDadataAddress = (dadataAddress: IResponseSuggestionData, value: string) => {
    return {
        areaName: dadataAddress.area,
        areaType: dadataAddress.area_type,
        blockName: dadataAddress.block,
        blockType: dadataAddress.block_type,
        cityName: dadataAddress.city,
        cityType: dadataAddress.city_type,
        cityKladrId: dadataAddress.city_kladr_id,
        country: dadataAddress.country,
        fiasId: dadataAddress.fias_id,
        flat: dadataAddress.flat,
        fullName: value,
        house: dadataAddress.house,
        postalCode: dadataAddress.postal_code,
        regionKladrId: dadataAddress.region_kladr_id,
        regionName: dadataAddress.region,
        regionType: dadataAddress.region_type,
        settlementName: dadataAddress.settlement,
        settlementType: dadataAddress.settlement_type,
        settlementKladrId: dadataAddress.settlement_kladr_id,
        streetName: dadataAddress.street,
        streetType: dadataAddress.street_type,
        geoLat: dadataAddress.geo_lat,
        geoLon: dadataAddress.geo_lon,
        kladrId: dadataAddress.kladr_id,
        qcGeo: dadataAddress.qc_geo,
    };
};

/** Функция возвращает массив подсказок для адреса */
export const getSuggestionAddress = async (address: string): Promise<ISuggestions[]> => {
    const {
        data: { suggestions },
    } = await httpClient.post<IAddressResponse>(DADATA_PROXY_SUGGEST_ADDRESS(), {
        query: address,
    });

    return suggestions.map(item => {
        const { data, value } = item;
        const isEndAddress = Boolean(
            data.region && data.postal_code && (data.city || data.settlement)
        );

        return {
            value,
            data: convertDadataAddress(data, value),
            label: item.value,
            id: item.value,
            isEndAddress,
        };
    });
};

/** Функция проверяет конечный это адресс или нет */
export const validateEndAddress = (value: string, data?: Partial<IFormattedSuggestionData>) =>
    !!(
        data?.fullName === value &&
        data?.regionName &&
        data?.postalCode &&
        (data?.cityName || data?.settlementName)
    );

export const validateEndDeliveryAddress = (
    value: string,
    data?: Partial<IFormattedSuggestionData>
) =>
    !!(
        data?.fullName === value &&
        data?.regionName &&
        data?.postalCode &&
        (data?.cityName || data?.settlementName) &&
        data?.house
    );

/**
 * Возвращает false если:
 * Если от dadata не пришло хотя бы одно поле из: kladr_id, geo_lat, geo_lon, qc_geo
 * Или если geo_lat, geo_lon имеет другие символы, кроме цифр и точки
 * Или если qc_geo не значение из списка 0,1,2,3,4,5
 */
export const validateGeoAddress = (data?: Partial<IFormattedSuggestionData>) => {
    const hasAllGeo = !!(data?.kladrId && data?.geoLat && data?.geoLon && data?.qcGeo);
    const qcGeoNumber = Number(data?.qcGeo);
    const isValidQcGeo =
        qcGeoNumber === 0 ||
        qcGeoNumber === 1 ||
        qcGeoNumber === 2 ||
        qcGeoNumber === 3 ||
        qcGeoNumber === 4 ||
        qcGeoNumber === 5;
    const geoRegExp = new RegExp('^([0-9.]+)$');
    const isValidGeoLat = data?.geoLat && geoRegExp.test(data.geoLat);
    const isValidGeoLon = data?.geoLon && geoRegExp.test(data.geoLon);

    return hasAllGeo && isValidQcGeo && isValidGeoLat && isValidGeoLon;
};

export const dataFieldName = (name: string) => `${name}${DATA_SUFFIX}`;

export const createAddressFieldValidator =
    (fieldName: string, message = EMPTY_MESSAGE) =>
    (value: string, allValues: unknown) => {
        const data = (allValues as Record<string, string | IFormattedSuggestionData>)[
            dataFieldName(fieldName)
        ];

        if (!data || typeof data === 'string') {
            return message;
        }

        return validateEndAddress(value, data) ? undefined : message;
    };

export const createDeliveryAddressFieldValidator =
    (fieldName: string) => (value?: string, allValues?: unknown) => {
        const data = (allValues as Record<string, string | IFormattedSuggestionData>)?.[
            dataFieldName(fieldName)
        ];

        // Если клиент не заполнил поле
        if (!value || (!(value.length > 1) && !data) || typeof data === 'string') {
            return 'Укажите адрес и выберите его из выпадающего меню';
        }

        // Если введенного адреса нет в подсказках dadata
        if (value.length > 1 && !data) {
            return 'Адрес не найден. Убедитесь, что указали верный адрес';
        }

        if (!validateEndDeliveryAddress(value, data)) {
            return 'Обязательно должны быть указаны: населенный пункт, улица и дом';
        }

        if (!validateGeoAddress(data)) {
            return 'Указанный адрес не входит в зону доставки. Попробуйте ввести другой адрес';
        }
    };

export const validatorMap = {
    address: createAddressFieldValidator,
    deliveryAddress: createDeliveryAddressFieldValidator,
};
